import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: {
      main: '#208040',
      light: '#60A080',
      dark: '#16602F',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#60A080',
      light: '#C0E0C0',
      dark: '#3E7A5A',
      contrastText: '#ffffff',
    },
    background: {
      default: '#F6FAF7',
      paper: '#ffffff',
    },
    text: {
      primary: '#0F1C14',
      secondary: '#42514A',
      disabled: '#8A958F',
    },
    divider: '#E3ECE6',
  },
  typography: {
    fontFamily: '"Montserrat", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    fontSize: 13,
    fontWeightLight: 300,
    fontWeightRegular: 400,
    fontWeightMedium: 500,
    fontWeightBold: 700,
    h1: { fontFamily: '"Montserrat", sans-serif', fontSize: '2.25rem', fontWeight: 700, lineHeight: 1.15, letterSpacing: '-0.01em' },
    h2: { fontFamily: '"Montserrat", sans-serif', fontSize: '1.5rem', fontWeight: 700, lineHeight: 1.2 },
    h3: { fontFamily: '"Montserrat", sans-serif', fontSize: '1.125rem', fontWeight: 700, lineHeight: 1.25 },
    h4: { fontFamily: '"Montserrat", sans-serif', fontSize: '1rem', fontWeight: 700, lineHeight: 1.3 },
    h5: { fontFamily: '"Montserrat", sans-serif', fontSize: '0.9375rem', fontWeight: 700, lineHeight: 1.35 },
    h6: { fontFamily: '"Montserrat", sans-serif', fontSize: '0.8125rem', fontWeight: 700, lineHeight: 1.4, textTransform: 'uppercase', letterSpacing: '0.04em' },
    body1: { fontSize: '0.8125rem', fontWeight: 400, lineHeight: 1.55 },
    body2: { fontSize: '0.75rem', fontWeight: 300, lineHeight: 1.55 },
    caption: { fontSize: '0.6875rem', fontWeight: 300, lineHeight: 1.4 },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.8125rem',
      letterSpacing: '0.01em',
    },
  },
  spacing: 6,
  shape: {
    borderRadius: 4,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontSize: '13px',
          fontVariantNumeric: 'tabular-nums',
        },
        '.num': {
          fontVariantNumeric: 'tabular-nums',
          fontFeatureSettings: '"tnum"',
          textAlign: 'right',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        size: 'small',
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 4,
          padding: '4px 12px',
          minHeight: 30,
        },
        sizeSmall: {
          padding: '3px 10px',
          minHeight: 26,
          fontSize: '0.75rem',
        },
        sizeMedium: {
          padding: '5px 14px',
          minHeight: 32,
        },
      },
    },
    MuiIconButton: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          padding: 4,
        },
        sizeSmall: {
          padding: 3,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: '0 1px 2px rgba(32,128,64,0.06)',
          border: '1px solid #E3ECE6',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '12px 14px',
          '&:last-child': {
            paddingBottom: 12,
          },
        },
      },
    },
    MuiChip: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          borderRadius: 4,
          height: 22,
          fontSize: '0.6875rem',
          fontWeight: 500,
        },
        sizeSmall: {
          height: 20,
          fontSize: '0.625rem',
        },
        label: {
          padding: '0 8px',
        },
        labelSmall: {
          padding: '0 6px',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '8px 12px',
          fontSize: '0.75rem',
          borderBottom: '1px solid #EDF4EF',
        },
        head: {
          fontWeight: 700,
          backgroundColor: '#F1F7F3',
          color: '#2F5C42',
          fontSize: '0.6875rem',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          padding: '10px 12px',
          fontFamily: '"Montserrat", sans-serif',
        },
        sizeSmall: {
          padding: '6px 10px',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 120ms ease',
          '&:nth-of-type(even)': {
            backgroundColor: '#FAFCFB',
          },
          '&:hover': {
            backgroundColor: '#EDF6EF !important',
          },
          '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: '#DDEEE2 !important',
          },
        },
        head: {
          backgroundColor: 'transparent !important',
          '&:hover': { backgroundColor: 'transparent !important' },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 4,
            fontSize: '0.8125rem',
          },
          '& .MuiInputLabel-root': {
            fontSize: '0.8125rem',
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          fontSize: '0.8125rem',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#D1DCD5',
            transition: 'border-color 120ms ease, box-shadow 120ms ease',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#60A080',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#208040',
            borderWidth: 1,
          },
          '&.Mui-focused': {
            boxShadow: '0 0 0 3px rgba(32,128,64,0.18)',
          },
        },
        input: {
          padding: '8px 12px',
        },
        inputSizeSmall: {
          padding: '6px 10px',
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.8125rem',
        },
        sizeSmall: {
          fontSize: '0.75rem',
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        select: {
          padding: '6px 10px',
          fontSize: '0.8125rem',
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          fontSize: '0.8125rem',
          minHeight: 32,
          padding: '6px 12px',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 6,
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: '48px !important',
          padding: '0 12px !important',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: '0 1px 2px rgba(32,128,64,0.08)',
          backgroundColor: '#208040',
          color: '#ffffff',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          borderRight: '1px solid #E3ECE6',
          backgroundColor: '#ffffff',
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          padding: '2px 8px',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          padding: '6px 12px',
          borderRadius: 4,
          margin: '1px 6px',
          minHeight: 36,
          '&.Mui-selected': {
            backgroundColor: '#C0E0C0',
            color: '#16602F',
            '& .MuiListItemIcon-root': { color: '#16602F' },
            '&:hover': {
              backgroundColor: '#A8D2B0',
            },
          },
          '&:hover': {
            backgroundColor: '#EDF6EF',
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          minWidth: 32,
          '& .MuiSvgIcon-root': {
            fontSize: '1.1rem',
          },
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontSize: '0.8125rem',
        },
        secondary: {
          fontSize: '0.6875rem',
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          width: 28,
          height: 28,
          fontSize: '0.75rem',
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 8,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1rem',
          fontWeight: 600,
          padding: '14px 18px',
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: '12px 18px',
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: '10px 18px',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontSize: '0.8125rem',
          minHeight: 40,
          padding: '8px 14px',
          textTransform: 'none',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 40,
        },
      },
    },
    MuiTablePagination: {
      styleOverrides: {
        root: {
          fontSize: '0.75rem',
        },
        selectLabel: {
          fontSize: '0.75rem',
        },
        displayedRows: {
          fontSize: '0.75rem',
        },
        select: {
          fontSize: '0.75rem',
        },
        toolbar: {
          minHeight: '40px !important',
        },
      },
    },
    MuiFormControl: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: '0.8125rem',
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: '0.6875rem',
          marginTop: 2,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: '0.6875rem',
          padding: '4px 8px',
        },
      },
    },
    MuiAlert: {
      defaultProps: { variant: 'standard' },
      styleOverrides: {
        root: {
          fontSize: '0.8125rem',
          padding: '8px 14px',
          borderRadius: 6,
          border: '1px solid transparent',
          alignItems: 'center',
        },
        icon: { fontSize: '1.05rem', padding: '4px 0' },
        message: { padding: '4px 0' },
        standardSuccess: {
          backgroundColor: '#EAF4ED',
          borderColor: '#C0E0C0',
          color: '#16602F',
          '& .MuiAlert-icon': { color: '#208040' },
        },
        standardInfo: {
          backgroundColor: '#E7F2EC',
          borderColor: '#C8DDD1',
          color: '#1F4D2F',
          '& .MuiAlert-icon': { color: '#3E7A5A' },
        },
        standardWarning: {
          backgroundColor: '#FBF1DE',
          borderColor: '#F0D9A6',
          color: '#8A5A10',
          '& .MuiAlert-icon': { color: '#E8A33D' },
        },
        standardError: {
          backgroundColor: '#FBEAE8',
          borderColor: '#F0C2BE',
          color: '#8A2E24',
          '& .MuiAlert-icon': { color: '#C74A3E' },
        },
      },
    },
    MuiSnackbarContent: {
      styleOverrides: {
        root: {
          backgroundColor: '#0F1C14',
          fontFamily: '"Montserrat", sans-serif',
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          fontSize: '0.625rem',
          minWidth: 16,
          height: 16,
        },
      },
    },
  },
});

export default theme;

// Status colors mapping — aligned with E3 green palette
export const statusColors: Record<string, string> = {
  NO_HISTORY: '#9CA9A1',
  LEAD: '#E8A33D',
  CONTACTED: '#60A080',
  IN_DIALOG: '#3E9AB5',
  OFFER_SENT: '#3E9AB5',
  NEGOTIATION: '#7A5AC7',
  WON: '#208040',
  ACTIVE: '#208040',
  INACTIVE: '#9CA9A1',
  LOST: '#C74A3E',
};

// Status labels (Danish)
export const statusLabels: Record<string, string> = {
  NO_HISTORY: 'Ingen historik',
  LEAD: 'Lead',
  CONTACTED: 'Kontaktet',
  IN_DIALOG: 'I dialog',
  OFFER_SENT: 'Tilbud sendt',
  NEGOTIATION: 'Forhandling',
  WON: 'Vundet',
  ACTIVE: 'Aktiv',
  INACTIVE: 'Inaktiv',
  LOST: 'Tabt',
};

// Priority colors
export const priorityColors: Record<string, string> = {
  LOW: '#9e9e9e',
  MEDIUM: '#2196f3',
  HIGH: '#ff9800',
  URGENT: '#f44336',
};

// Activity type icons
export const activityTypeLabels: Record<string, string> = {
  CALL: 'Opkald',
  EMAIL: 'Email',
  MEETING: 'Møde',
  NOTE: 'Note',
  STATUS_CHANGE: 'Statusændring',
  SYSTEM: 'System',
};
